<script setup>
import { onMounted, onBeforeUnmount, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { internalCall } from 'dashboard/lib/internalCallSession';
import CallControl from './CallControl.vue';

defineEmits(['close']);
const { t } = useI18n();
const state = internalCall.state;
const devices = ref([]);
const testing = ref(false);
const testPending = ref(false);
const level = ref(0);
const pending = ref(false);
const error = ref(false);
const heading = ref(null);
let stream;
let context;
let frame;
let testRevision = 0;
const outputSupported = 'setSinkId' in HTMLMediaElement.prototype;
const listDevices = async () => {
  devices.value = await navigator.mediaDevices.enumerateDevices();
};
const deviceLabel = (device, index) => {
  if (!device.label)
    return t('INTERNAL_CALL.DEVICE_NUMBER', { number: index + 1 });
  return device.label.replace(/^(Default|Communications)(?= - |$)/i, label =>
    t(
      label.toLowerCase() === 'default'
        ? 'INTERNAL_CALL.DEFAULT_DEVICE'
        : 'INTERNAL_CALL.COMMUNICATIONS_DEVICE'
    )
  );
};
const update = async patch => {
  if (pending.value) return;
  const trigger = heading.value.ownerDocument.activeElement;
  heading.value.focus();
  pending.value = true;
  error.value = false;
  try {
    await internalCall.updateSettings(patch);
  } catch {
    error.value = true;
  } finally {
    pending.value = false;
    await nextTick();
    // A rejected change leaves reactive settings unchanged, so Vue may not
    // patch the native control back to its accepted value.
    const value = state.settings[Object.keys(patch)[0]];
    if (trigger?.type === 'checkbox') trigger.checked = value;
    else if (trigger?.tagName === 'SELECT') trigger.value = value;
    if (
      trigger?.isConnected &&
      heading.value?.ownerDocument.activeElement === heading.value
    )
      trigger.focus();
  }
};
const stopTest = () => {
  testRevision += 1;
  stream?.getTracks().forEach(track => track.stop());
  context?.close();
  cancelAnimationFrame(frame);
  stream = null;
  context = null;
  testing.value = false;
  testPending.value = false;
  level.value = 0;
};
const testMicrophone = async () => {
  if (testing.value || testPending.value) {
    stopTest();
    return;
  }
  testRevision += 1;
  const revision = testRevision;
  testPending.value = true;
  error.value = false;
  try {
    const captured = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: state.settings.microphoneId
          ? { exact: state.settings.microphoneId }
          : undefined,
        noiseSuppression: state.settings.noiseSuppression,
        echoCancellation: true,
      },
    });
    if (revision !== testRevision) {
      captured.getTracks().forEach(track => track.stop());
      return;
    }
    stream = captured;
    context = new AudioContext();
    const analyser = context.createAnalyser();
    context.createMediaStreamSource(stream).connect(analyser);
    analyser.fftSize = 256;
    const samples = new Uint8Array(analyser.frequencyBinCount);
    const measure = () => {
      analyser.getByteTimeDomainData(samples);
      level.value = Math.min(
        100,
        Math.max(...samples.map(value => Math.abs(value - 128))) * 4
      );
      frame = requestAnimationFrame(measure);
    };
    measure();
    testPending.value = false;
    testing.value = true;
    await listDevices();
  } catch {
    if (revision !== testRevision) return;
    stopTest();
    error.value = true;
  }
};
onMounted(async () => {
  heading.value?.focus();
  try {
    await listDevices();
  } catch {
    error.value = true;
  }
  navigator.mediaDevices?.addEventListener('devicechange', listDevices);
});
onBeforeUnmount(() => {
  stopTest();
  navigator.mediaDevices?.removeEventListener('devicechange', listDevices);
});
</script>

<template>
  <section
    class="flex min-h-0 flex-1 flex-col overflow-y-auto p-4 text-n-slate-12"
    :aria-label="t('INTERNAL_CALL.SETTINGS')"
    :aria-busy="pending"
    @keydown.esc.stop="$emit('close')"
  >
    <header class="mb-5 flex items-center justify-between gap-2">
      <h3
        ref="heading"
        tabindex="-1"
        class="m-0 text-base font-semibold outline-none"
      >
        {{ t('INTERNAL_CALL.SETTINGS') }}
      </h3>
      <CallControl
        icon="i-lucide-x"
        :label="t('INTERNAL_CALL.CLOSE_PANEL')"
        @click="$emit('close')"
      />
    </header>
    <p v-if="pending" role="status" class="mb-3 text-xs text-n-slate-11">
      {{ t('INTERNAL_CALL.SETTINGS_PENDING') }}
    </p>
    <p
      v-if="error"
      role="alert"
      class="mb-4 rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
    >
      {{ t('INTERNAL_CALL.ERROR.media_failed') }}
    </p>
    <fieldset :disabled="pending" class="min-w-0 space-y-4">
      <legend
        class="mb-3 text-xs font-semibold uppercase tracking-wide text-n-slate-11"
      >
        {{ t('INTERNAL_CALL.AUDIO_SETTINGS') }}
      </legend>
      <label class="block text-sm font-medium">
        {{ t('INTERNAL_CALL.MICROPHONE') }}
        <span class="relative mt-2 block">
          <select
            class="m-0 w-full appearance-none rounded-lg border border-n-strong bg-n-field !bg-none py-2.5 pl-3 pr-10 text-sm"
            :value="state.settings.microphoneId"
            @change="
              stopTest();
              update({ microphoneId: $event.target.value });
            "
          >
            <option value="">{{ t('INTERNAL_CALL.DEFAULT_DEVICE') }}</option>
            <option
              v-for="(device, index) in devices.filter(
                item => item.kind === 'audioinput'
              )"
              :key="device.deviceId"
              :value="device.deviceId"
            >
              {{ deviceLabel(device, index) }}
            </option>
          </select>
          <span
            class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 i-lucide-chevron-down text-n-slate-11"
            aria-hidden="true"
          />
        </span>
      </label>
      <div class="rounded-xl border border-n-weak p-3">
        <button
          type="button"
          class="text-sm font-medium text-n-blue-11 underline-offset-4 hover:underline focus-visible:outline-n-brand"
          :aria-pressed="testing"
          @click="testMicrophone"
        >
          <template v-if="testPending">
            {{ t('INTERNAL_CALL.CANCEL_TEST') }}
          </template>
          <template v-else>
            {{
              t(
                testing
                  ? 'INTERNAL_CALL.STOP_TEST'
                  : 'INTERNAL_CALL.TEST_MICROPHONE'
              )
            }}
          </template>
        </button>
        <p role="status" class="m-0 text-xs leading-5 text-n-slate-11">
          <span v-if="testPending" class="mt-2 block">
            {{ t('INTERNAL_CALL.TEST_PENDING') }}
          </span>
        </p>
        <meter
          v-show="testing"
          :value="level"
          min="0"
          max="100"
          class="mt-2 h-3 w-full"
          :aria-label="t('INTERNAL_CALL.MICROPHONE_LEVEL')"
        />
        <p class="mb-0 mt-2 text-xs leading-5 text-n-slate-11">
          {{ t('INTERNAL_CALL.TEST_HELP') }}
        </p>
      </div>
      <label
        class="flex items-center justify-between gap-4 rounded-xl bg-n-alpha-1 p-3 text-sm font-medium"
      >
        <span
          >{{ t('INTERNAL_CALL.NOISE_SUPPRESSION')
          }}<span class="mt-1 block text-xs font-normal text-n-slate-11">{{
            t('INTERNAL_CALL.NOISE_HELP')
          }}</span></span
        >
        <input
          type="checkbox"
          class="size-5 shrink-0 accent-n-blue-9"
          :checked="state.settings.noiseSuppression"
          @change="
            stopTest();
            update({ noiseSuppression: $event.target.checked });
          "
        />
      </label>
      <label class="block text-sm font-medium">
        {{ t('INTERNAL_CALL.OUTPUT_DEVICE') }}
        <span class="relative mt-2 block">
          <select
            :disabled="!outputSupported"
            class="m-0 w-full appearance-none rounded-lg border border-n-strong bg-n-field !bg-none py-2.5 pl-3 pr-10 text-sm disabled:opacity-50"
            :value="state.settings.outputId"
            @change="update({ outputId: $event.target.value })"
          >
            <option value="default">
              {{ t('INTERNAL_CALL.DEFAULT_DEVICE') }}
            </option>
            <option
              v-for="(device, index) in devices.filter(
                item => item.kind === 'audiooutput'
              )"
              :key="device.deviceId"
              :value="device.deviceId"
            >
              {{ deviceLabel(device, index) }}
            </option>
          </select>
          <span
            class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 i-lucide-chevron-down text-n-slate-11"
            aria-hidden="true"
          />
        </span>
        <span
          v-if="!outputSupported"
          class="mt-1 block text-xs font-normal text-n-slate-11"
          >{{ t('INTERNAL_CALL.OUTPUT_SYSTEM') }}</span
        >
      </label>
    </fieldset>
    <fieldset
      :disabled="pending"
      class="mt-6 min-w-0 space-y-4 border-t border-n-weak pt-5"
    >
      <legend
        class="text-xs font-semibold uppercase tracking-wide text-n-slate-11"
      >
        {{ t('INTERNAL_CALL.VIDEO_SETTINGS') }}
      </legend>
      <label class="block text-sm font-medium">
        {{ t('INTERNAL_CALL.CAMERA') }}
        <span class="relative mt-2 block">
          <select
            class="m-0 w-full appearance-none rounded-lg border border-n-strong bg-n-field !bg-none py-2.5 pl-3 pr-10 text-sm"
            :value="state.settings.cameraId"
            @change="update({ cameraId: $event.target.value })"
          >
            <option value="">{{ t('INTERNAL_CALL.DEFAULT_DEVICE') }}</option>
            <option
              v-for="(device, index) in devices.filter(
                item => item.kind === 'videoinput'
              )"
              :key="device.deviceId"
              :value="device.deviceId"
            >
              {{ deviceLabel(device, index) }}
            </option>
          </select>
          <span
            class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 i-lucide-chevron-down text-n-slate-11"
            aria-hidden="true"
          />
        </span>
      </label>
      <label class="block text-sm font-medium">
        {{ t('INTERNAL_CALL.STREAM_QUALITY') }}
        <span class="relative mt-2 block">
          <select
            class="m-0 w-full appearance-none rounded-lg border border-n-strong bg-n-field !bg-none py-2.5 pl-3 pr-10 text-sm"
            :value="state.settings.quality"
            @change="update({ quality: $event.target.value })"
          >
            <option value="auto">{{ t('INTERNAL_CALL.QUALITY_AUTO') }}</option>
            <option value="text">{{ t('INTERNAL_CALL.QUALITY_TEXT') }}</option>
            <option value="motion">
              {{ t('INTERNAL_CALL.QUALITY_MOTION') }}
            </option>
          </select>
          <span
            class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 i-lucide-chevron-down text-n-slate-11"
            aria-hidden="true"
          />
        </span>
      </label>
      <label
        class="flex items-center justify-between gap-4 text-sm font-medium"
      >
        {{ t('INTERNAL_CALL.SHARE_AUDIO') }}
        <input
          type="checkbox"
          class="size-5 accent-n-blue-9"
          :checked="state.settings.screenAudio"
          @change="update({ screenAudio: $event.target.checked })"
        />
      </label>
      <p class="text-xs leading-5 text-n-slate-11">
        {{ t('INTERNAL_CALL.SHARE_AUDIO_HELP') }}
      </p>
      <button
        v-if="state.screen"
        type="button"
        class="flex min-h-10 w-full items-center justify-center gap-2 rounded-lg border border-n-strong px-3 text-sm font-medium hover:bg-n-alpha-2 focus-visible:outline-n-brand"
        @click="internalCall.run(() => internalCall.replaceScreen())"
      >
        <span class="i-lucide-monitor-up size-4" aria-hidden="true" />{{
          t('INTERNAL_CALL.REPLACE_SCREEN')
        }}
      </button>
    </fieldset>
  </section>
</template>
